import { useCallback, useEffect, useState } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { flushOfflineQueue, offlineDb, queueMutation } from "@/db/offlineDb";
import { uuid } from "@/lib/uuid";

export function useOfflineSync(token, terminalId) {
  const [serverOnline, setServerOnline] = useState(navigator.onLine);
  const pendingCount = useLiveQuery(() => offlineDb.offline_sync_queue.where("status").equals("pending").count(), [], 0);
  const flushQueue = useCallback(() => flushOfflineQueue(token), [token]);
  const queueAction = useCallback((type, entityId = uuid(), payload = {}) => queueMutation(type, entityId, payload, terminalId), [terminalId]);

  const checkServer = useCallback(async () => {
    if (!navigator.onLine) { setServerOnline(false); return false; }
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/`, { cache: "no-store", credentials: "include" });
      setServerOnline(response.ok);
      return response.ok;
    } catch {
      setServerOnline(false);
      return false;
    }
  }, []);

  useEffect(() => {
    const handleOnline = () => checkServer().then(online => online && flushQueue());
    const handleOffline = () => setServerOnline(false);
    const handleWorkerMessage = event => event.data?.type === "QUEUE_FLUSHED" && checkServer();
    checkServer();
    const poll = window.setInterval(checkServer, 10000);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    navigator.serviceWorker?.addEventListener("message", handleWorkerMessage);
    return () => {
      window.clearInterval(poll);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      navigator.serviceWorker?.removeEventListener("message", handleWorkerMessage);
    };
  }, [checkServer, flushQueue]);

  useEffect(() => {
    if (serverOnline) {
      flushQueue();
    }
  }, [serverOnline, flushQueue]);

  return { serverOnline, pendingCount, flushQueue, queueAction, checkServer };
}